import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
} from '@angular/core';
import { InceptionService, TranslationService } from '@proximus/ng-common';
import { UpdateModemSafetySettings } from '../../../../../store/actions/internet-settings.actions';
import { Store } from '@ngrx/store';
import {
  ApplicationState,
  getInternetSettingsModemSafety,
} from '../../../../../store/reducers';
import { Alert, AlertsService } from '@proximus/ng-common-components';
import { OnDestroyHookDirective } from '../../../../../shared/hooks';

@Component({
  selector: 'mprd-modem-safety',
  templateUrl: './modem-safety.component.html',
})
export class ModemSafetyComponent
  extends OnDestroyHookDirective
  implements OnInit, OnChanges, AfterViewInit
{
  readonly BASIC = 'BASIC';
  readonly NORMAL = 'NORMAL';

  @Input() loginId: string;
  @Input() technicalProfile: string;

  @ViewChild('lowSecurity', { static: false }) radioLowSecurity: ElementRef;
  @ViewChild('highSecurity', { static: false }) radioHighSecurity: ElementRef;

  updateModemSafetySuccess = false;
  updateModemSafetyFailed = false;
  updatedModemSafetyProfile;

  cacheProfile: string;

  constructor(
    private inceptionService: InceptionService,
    private elementRef: ElementRef,
    private store: Store<ApplicationState>,
    private alertService: AlertsService,
    private translationService: TranslationService,
  ) {
    super();
  }

  ngOnInit() {
    this.inceptionService.init();
    this.addSubscription(
      this.store.select(getInternetSettingsModemSafety).subscribe((data) => {
        this.updateModemSafetySuccess = data.updateSuccess;
        this.updateModemSafetyFailed = data.updateError;
        this.updatedModemSafetyProfile = data.profile.technicalProfile;
        this.handleState();
      }),
    );
  }

  ngAfterViewInit() {
    this.updateRadioButtons();
  }

  ngOnChanges(): void {
    this.cacheProfile = this.technicalProfile;
  }

  get isBasic() {
    return this.cacheProfile === this.BASIC;
  }

  set cacheProfileSelect(profile) {
    this.cacheProfile = profile;
  }

  get profileChanged() {
    return this.technicalProfile !== this.cacheProfile;
  }

  dispatchModemSafetySettings() {
    this.technicalProfile = this.cacheProfile;
    this.store.dispatch(
      new UpdateModemSafetySettings({
        login: this.loginId,
        technicalProfile: this.technicalProfile,
      }),
    );
  }

  updateRadioButtons() {
    if (this.technicalProfile) {
      this.radioLowSecurity.nativeElement.checked = this.isBasic;
      this.radioHighSecurity.nativeElement.checked = !this.isBasic;
    }
  }

  private handleState() {
    if (this.updateModemSafetyFailed) {
      this.technicalProfile =
        this.technicalProfile === this.BASIC ? this.NORMAL : this.BASIC;
      this.cacheProfile = this.technicalProfile;
      this.updateRadioButtons();
      this.dispatchAlert(
        this.updateModemSafetySuccess,
        this.updateModemSafetyFailed,
      );
    } else if (this.updateModemSafetySuccess) {
      this.cacheProfile = this.updatedModemSafetyProfile;
      this.technicalProfile = this.updatedModemSafetyProfile;
      this.updateRadioButtons();
      this.dispatchAlert(
        this.updateModemSafetySuccess,
        this.updateModemSafetyFailed,
      );
    }
  }

  private dispatchAlert(
    updateModemSafetySuccess: boolean,
    updateModemSafetyFailed: boolean,
  ): void {
    let title: string;
    let description: string;
    let type: 'error' | 'info' | 'success' | 'warning';
    let taggingData: any;

    if (updateModemSafetySuccess) {
      title = this.translationService.getTranslatedValueFor(
        'internet.settings.safety.update.success.title',
      );
      description = this.translationService.getTranslatedValueFor(
        'internet.settings.safety.update.success',
      );
      type = 'success';
      taggingData = {
        event: 'popup-impression',
        popup: {
          zone:
            (this.cacheProfile === 'NORMAL' ? 'high' : 'low') +
            ' ' +
            this.translationService.getTranslatedValueFor(
              'internet.settings.safety.title',
            ),
          name: 'change success',
        },
      };
    } else if (updateModemSafetyFailed) {
      title = this.translationService.getTranslatedValueFor(
        'internet.settings.safety.update.failed.title',
      );
      description = this.translationService.getTranslatedValueFor(
        'internet.settings.safety.update.failed',
      );
      type = 'error';
      taggingData = {
        event: 'popup-impression',
        popup: {
          zone:
            (this.cacheProfile === 'NORMAL' ? 'high' : 'low') +
            ' ' +
            this.translationService.getTranslatedValueFor(
              'internet.settings.safety.title',
            ),
          name: 'change error',
        },
        error: {
          type: 'backend',
          message: description,
        },
      };
    }
    const alert: Alert = {
      isClosable: true,
      alertKey: 'updateModemSafetyAlert',
      title,
      description,
      type,
      taggingData,
    };
    this.alertService.dispatchAlert(alert);
  }
}
